/**
 * <mega-menu> — self-contained header mega menu.
 * No Woolman dependency, no transitionend races.
 *
 * Single state object -> single render(). Every visual state is derived from
 * { open, pane }, so it can't desync and can't "open then reverse".
 *
 * Interaction contract (data-attributes in the markup):
 *   [data-mm-toggle] – mobile hamburger: open at top level / close
 *   [data-mm-open="<handle>"] – open the panel and activate that pane
 *   [data-mm-back]   – (mobile) return from a pane to the top level
 *   [data-mm-close]  – close everything (also the overlay)
 */
const MEGA_MENU_VERSION = 'v1';

class MegaMenu extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.panel = this.querySelector('[data-mm-panel]');
    this.drawer = this.querySelector('.mm-drawer');
    this.titleEl = this.querySelector('[data-mm-title]');
    this.panes = Array.from(this.querySelectorAll('[data-mm-pane]'));
    this.desktop = window.matchMedia('(min-width: 1024px)');
    this.state = { open: false, pane: null };
    this.lastFocus = null;
    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener('click', (e) => {
      const el = e.target.closest(
        '[data-mm-open],[data-mm-toggle],[data-mm-close],[data-mm-back]'
      );
      if (!el || !this.contains(el)) return;

      if (el.hasAttribute('data-mm-open')) {
        e.preventDefault();
        this.openPane(el.getAttribute('data-mm-open'));
      } else if (el.hasAttribute('data-mm-toggle')) {
        e.preventDefault();
        if (this.state.open) {
          this.close();
        } else if (this.desktop.matches) {
          // Desktop: open straight into the first pane (e.g. Shop)
          const firstPane = this.panes[0];
          firstPane ? this.openPane(firstPane.getAttribute('data-mm-pane')) : this.openMenu();
        } else {
          // Mobile: open at the top level; tapping an item then drills in
          this.openMenu();
        }
      } else if (el.hasAttribute('data-mm-close')) {
        e.preventDefault();
        this.close();
      } else if (el.hasAttribute('data-mm-back')) {
        e.preventDefault();
        this.back();
      }
    });

    this.desktop.addEventListener('change', () => this.render());
    document.documentElement.setAttribute('data-mega-menu', MEGA_MENU_VERSION);
    this.render();
  }

  /* ---- actions -------------------------------------------------------- */

  openMenu() {
    this.rememberFocus();
    this.state.open = true;
    this.state.pane = null;
    this.render();
    this.focusFirst();
  }

openPane(pane) {
    // Re-clicking the currently open trigger collapses just that submenu.
    if (this.state.open && this.state.pane === pane) {
      this.back();
      return;
    }

    // Switching to a DIFFERENT pane while one is open:
    // collapse the current submenu first, then open the new one.
    if (this.state.open && this.state.pane && this.state.pane !== pane) {
      this.state.pane = null;      // collapse current (drawer narrows back)
      this.render();
      clearTimeout(this._switchTimer);
      this._switchTimer = setTimeout(() => {
        this.state.pane = pane;    // open the new one
        this.render();
        this.focusFirst();
      }, 300);                     // ~matches the grow/collapse duration
      return;
    }

    // Opening from closed (or from top-level with no pane yet)
    this.rememberFocus();
    this.state.open = true;
    this.state.pane = pane;
    this.render();
    this.focusFirst();
  }
  back() {
    this.state.pane = null;
    this.render();
  }

  close() {
    this.state.open = false;
    this.state.pane = null;
    this.render();
    this.restoreFocus();
  }

  /* ---- single renderer ------------------------------------------------ */

  render() {
    const { open, pane } = this.state;

    this.classList.toggle('mm--open', open);
    this.classList.toggle('mm--drilled', !!pane);

    this.panes.forEach((p) =>
      p.classList.toggle('mm--active', p.getAttribute('data-mm-pane') === pane)
    );

    const active = this.panes.find((p) => p.getAttribute('data-mm-pane') === pane);
    if (this.titleEl) {
      this.titleEl.textContent = active ? active.getAttribute('data-mm-pane-title') : '';
    }

    this.querySelectorAll('[data-mm-open]').forEach((t) =>
      t.setAttribute('aria-expanded', String(open && t.getAttribute('data-mm-open') === pane))
    );
    this.querySelectorAll('[data-mm-toggle]').forEach((t) =>
      t.setAttribute('aria-expanded', String(open))
    );
    if (this.panel) this.panel.setAttribute('aria-hidden', String(!open));

    document.documentElement.classList.toggle('mm-scroll-lock', open);

    if (open) document.addEventListener('keydown', this.onKeydown);
    else document.removeEventListener('keydown', this.onKeydown);
  }

  /* ---- keyboard + focus ---------------------------------------------- */

  onKeydown(e) {
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key === 'Tab') this.trapTab(e);
  }

  focusable() {
    return Array.from(
      this.drawer.querySelectorAll(
        'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null); // visible only
  }

  focusFirst() {
    requestAnimationFrame(() => {
      const f = this.focusable();
      (f[0] || this.drawer).focus();
    });
  }

  trapTab(e) {
    const f = this.focusable();
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  rememberFocus() {
    if (!this.state.open) this.lastFocus = document.activeElement;
  }

  restoreFocus() {
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    this.lastFocus = null;
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}